import React, { useCallback } from 'react';
import AdminCrudListPage from '../../../components/admin/AdminCrudListPage';
import KkpColorBadge from '../../../components/kkp/KkpColorBadge';
import { AppButton, AppStatusBadge } from '../../../components/ui';
import secteursAdminService from '../../../services/secteursAdminService';
import { notify } from '../../../components/common/AppToast';
import { ROUTES } from '../../../config/routes';

const COLUMNS = [
  {
    key: 'nom',
    label: 'Nom',
    render: (row) => <strong>{row.nom}</strong>,
  },
  {
    key: 'photo',
    label: 'Photo',
    sortable: false,
    render: (row) => (
      row.has_photo ? (
        <span
          className={row.has_custom_photo ? 'text-success' : 'text-muted'}
          title={row.has_custom_photo ? 'Photo admin' : 'Photo historique'}
        >
          Oui
        </span>
      ) : (
        <span className="text-muted">—</span>
      )
    ),
  },
  {
    key: 'email',
    label: 'Email',
  },
  {
    key: 'compte',
    label: 'Compte',
    getValue: (row) => row.user?.fullName || row.user?.email || '',
    render: (row) => (
      row.user ? (
        <span title={row.user.email}>{row.user.fullName || row.user.email}</span>
      ) : (
        <span className="text-warning">Non lié</span>
      )
    ),
  },
  {
    key: 'telephone',
    label: 'Téléphone',
    render: (row) => row.telephone || <span className="text-muted">—</span>,
  },
  {
    key: 'couleur',
    label: 'Couleur',
    render: (row) => (
      row.couleur ? (
        <KkpColorBadge color={row.couleur} vivid>{row.couleur}</KkpColorBadge>
      ) : (
        <span className="text-muted">—</span>
      )
    ),
  },
  {
    key: 'active',
    label: 'Statut',
    sortType: 'number',
    getValue: (row) => (row.active ? 1 : 0),
    render: (row) => <AppStatusBadge status={row.active ? 'active' : 'inactive'} />,
  },
];

const SecteursAdminCommerciauxPage = () => {
  const load = useCallback(async (page, _q, { limit } = {}) => {
    const result = await secteursAdminService.listCommerciaux({
      page,
      limit: limit ?? 20,
    });
    return { rows: result.commerciaux, pagination: result.pagination };
  }, []);

  const handleToggleActive = async (commercial, { ask, reload }) => {
    const next = !commercial.active;
    const label = next ? 'réactiver' : 'désactiver';
    const ok = await ask({
      title: `${label.charAt(0).toUpperCase() + label.slice(1)} ce commercial ?`,
      message: `Voulez-vous ${label} « ${commercial.nom} » ?`,
      confirmLabel: next ? 'Réactiver' : 'Désactiver',
      confirmVariant: next ? 'primary' : 'warning',
    });
    if (!ok) return;
    try {
      await secteursAdminService.updateCommercial(commercial.id, { active: next });
      notify.success(next ? 'Commercial réactivé' : 'Commercial désactivé');
      reload();
    } catch (err) {
      notify.error(err.message);
    }
  };

  return (
    <AdminCrudListPage
      title="Gestion des commerciaux"
      newTo={ROUTES.admin.commercialNew}
      newLabel="Nouveau commercial"
      columns={COLUMNS}
      load={load}
      emptyMessage="Aucun commercial trouvé"
      rowClassName={(row) => (row.active ? undefined : 'table-secondary')}
      editTo={(row) => ROUTES.admin.commercialEdit(row.id)}
      onDelete={(row) => secteursAdminService.deleteCommercial(row.id)}
      deleteConfirm={(row) => ({
        title: 'Supprimer ce commercial ?',
        message: `Supprimer définitivement « ${row.nom} » ?`,
        successMessage: 'Commercial supprimé',
      })}
      extraActions={(c, helpers) => (
        <AppButton
          size="sm"
          variant={c.active ? 'outline-warning' : 'outline-success'}
          icon={c.active ? 'mdi:pause' : 'mdi:play'}
          title={c.active ? 'Désactiver' : 'Réactiver'}
          onClick={() => handleToggleActive(c, helpers)}
        />
      )}
    />
  );
};

export default SecteursAdminCommerciauxPage;
